import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { Alert, AlertDescription, AlertTitle } from '../components/ui/alert';
import { AlertTriangle, FlaskConical, ChevronRight, Upload, ShieldAlert, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';
import ProductPicker from '../components/ProductPicker';
import { beverageProductsService } from '../services';

/**
 * Phase 3 Step 5 — Consolidated Recipes admin page.
 *
 * Three tabs:
 *   1. Settings & Pilot — feature flag toggle (with confirmation), dry-run.
 *   2. Recipes          — list of approved recipes, view/edit detail.
 *   3. Imports          — pending Excel previews, auto-map suggestions, approve.
 *
 * Lives at /settings/recipes. Admin-only — non-admins are blocked at the
 * route layer; this page additionally hides destructive actions when
 * `me.role !== 'admin'`.
 */
export default function RecipesAdminPage() {
  const [me, setMe] = useState(null);
  const [tab, setTab] = useState('settings');
  const navigate = useNavigate();

  useEffect(() => {
    api.get('/auth/me').then(r => setMe(r.data)).catch(() => setMe(null));
  }, []);

  // iter434 — honour multi-role users. Some operators (e.g. an
  // Auditor who is ALSO Admin) carry `role='AUDITOR'` as their primary
  // role but `roles=['auditor','admin']` on the multi-role column.
  // The previous single-role check left those admins with greyed-out
  // mutating buttons on this page.
  //
  // iter443 — TEMPORARY: While auditors are running end-to-end testing
  // of the Stocktake module, the variable below treats ANY internal
  // user (admin / senior_scheduler / scheduler / auditor) as "admin"
  // so the mutating buttons on this page are enabled for them. Revert
  // to the admin-only check below once testing is complete:
  //   const isAdmin = (() => {
  //     if (!me) return false;
  //     if ((me.role || '').toLowerCase() === 'admin') return true;
  //     const list = Array.isArray(me.roles) ? me.roles : [];
  //     return list.some(r => (r || '').toLowerCase() === 'admin');
  //   })();
  const isAdmin = (() => {
    if (!me) return false;
    const single = (me.role || '').toLowerCase();
    const list = Array.isArray(me.roles) ? me.roles.map(r => (r || '').toLowerCase()) : [];
    const ALLOWED = new Set(['admin', 'senior_scheduler', 'scheduler', 'auditor']);
    if (ALLOWED.has(single)) return true;
    return list.some(r => ALLOWED.has(r));
  })();

  return (
    <div className="container mx-auto px-4 py-6 max-w-6xl" data-testid="recipes-admin-page">
      <header className="mb-6 flex items-start gap-3">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate('/stocktaking/dashboard')}
          data-testid="back-to-stocktaking"
          className="mt-1"
        >
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Recipes &amp; Post-Mix</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Recipe foundation, post-mix imports, and pilot controls. Recipe
            expansion is OFF by default and must be enabled per organisation
            by an admin.
          </p>
        </div>
      </header>

      <Tabs value={tab} onValueChange={setTab} className="w-full">
        <TabsList className="grid w-full grid-cols-4" data-testid="recipes-admin-tabs">
          <TabsTrigger value="settings" data-testid="tab-settings">Settings &amp; Pilot</TabsTrigger>
          <TabsTrigger value="pilot-sites" data-testid="tab-pilot-sites">Pilot Sites</TabsTrigger>
          <TabsTrigger value="recipes" data-testid="tab-recipes">Recipes</TabsTrigger>
          <TabsTrigger value="imports" data-testid="tab-imports">Imports</TabsTrigger>
        </TabsList>
        <TabsContent value="settings"><SettingsTab isAdmin={isAdmin} /></TabsContent>
        <TabsContent value="pilot-sites"><PilotSitesTab isAdmin={isAdmin} /></TabsContent>
        <TabsContent value="recipes"><RecipesTab isAdmin={isAdmin} /></TabsContent>
        <TabsContent value="imports"><ImportsTab isAdmin={isAdmin} /></TabsContent>
      </Tabs>
    </div>
  );
}

/* ─────────────────────── Settings & Pilot ─────────────────────── */

function SettingsTab({ isAdmin }) {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [pendingValue, setPendingValue] = useState(false);
  const [dryRunSessionId, setDryRunSessionId] = useState('');
  const [dryRun, setDryRun] = useState(null);
  const [dryRunLoading, setDryRunLoading] = useState(false);

  const reload = () =>
    api.get('/settings').then(r => setSettings(r.data)).catch(e => toast.error('Failed to load settings'));

  useEffect(() => { reload(); }, []);

  const handleToggleClick = (next) => {
    if (!isAdmin) return;
    setPendingValue(next);
    setConfirmOpen(true);
  };

  const confirmToggle = async () => {
    setLoading(true);
    try {
      const r = await api.put('/settings', { recipe_expansion_enabled: pendingValue });
      setSettings(r.data);
      toast.success(`Recipe expansion ${pendingValue ? 'ENABLED' : 'disabled'}`);
    } catch (e) {
      toast.error(`Failed to toggle: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setLoading(false);
      setConfirmOpen(false);
    }
  };

  const runDryRun = async () => {
    if (!dryRunSessionId) { toast.error('Enter a session ID'); return; }
    setDryRunLoading(true);
    setDryRun(null);
    try {
      const r = await api.post('/recipes/dry-run-session', {
        session_id: parseInt(dryRunSessionId, 10),
      });
      setDryRun(r.data);
      toast.success('Dry-run complete');
    } catch (e) {
      toast.error(`Dry-run failed: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setDryRunLoading(false);
    }
  };

  const flagOn = !!settings?.recipe_expansion_enabled;

  return (
    <div className="space-y-4 mt-4">
      <Card data-testid="recipe-flag-card">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <FlaskConical className="h-5 w-5" />
              Recipe Expansion (Pilot)
            </CardTitle>
            <Badge variant={flagOn ? 'default' : 'secondary'} data-testid="recipe-flag-badge">
              {flagOn ? 'ENABLED' : 'OFF'}
            </Badge>
          </div>
        </CardHeader>
        <CardContent>
          <Alert variant={flagOn ? 'destructive' : 'default'} className="mb-4">
            <ShieldAlert className="h-4 w-4" />
            <AlertTitle>Advanced / pilot feature</AlertTitle>
            <AlertDescription className="text-sm">
              <strong>Scope:</strong> this flag is{' '}
              <strong>organisation-wide</strong>. When enabled, sales of
              recipe output products (e.g. "Coca Cola 22oz Glass") expand
              into ingredient deductions (e.g. syrup BIB + carbonated
              water) for <em>every site</em> in this organisation.
              <br /><br />
              <strong>For a single-site pilot</strong>, leave this flag
              OFF and use the <strong>Pilot Sites</strong> tab to enable
              recipe expansion for one site at a time (or carve a single
              site out of an org-wide rollout). Use the Dry-run card
              below for an OFF-vs-ON comparison on any session without
              changing live calculations.
            </AlertDescription>
          </Alert>

          <div className="flex items-center gap-3 flex-wrap">
            <Button
              variant={flagOn ? 'destructive' : 'default'}
              disabled={!isAdmin || loading}
              onClick={() => handleToggleClick(!flagOn)}
              data-testid="toggle-recipe-expansion-btn"
            >
              {loading ? '...' : flagOn ? 'Disable Recipe Expansion' : 'Enable Recipe Expansion'}
            </Button>
            {!isAdmin && (
              <span className="text-xs text-muted-foreground">
                Only Admins can toggle this flag.
              </span>
            )}
          </div>
        </CardContent>
      </Card>

      <Card data-testid="dry-run-card">
        <CardHeader><CardTitle>Pilot Dry-run</CardTitle></CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground mb-3">
            Recalculate one session twice — once with recipe expansion OFF
            and once ON — and compare results side-by-side. The
            organisation's flag is NOT changed; the persisted snapshot
            reflects flag-OFF.
          </p>
          <div className="flex items-end gap-3 flex-wrap">
            <div>
              <Label htmlFor="dryrun-session-id">Session ID</Label>
              <Input
                id="dryrun-session-id"
                type="number"
                placeholder="e.g. 61"
                value={dryRunSessionId}
                onChange={e => setDryRunSessionId(e.target.value)}
                data-testid="dryrun-session-input"
                className="w-40"
              />
            </div>
            <Button
              onClick={runDryRun}
              disabled={dryRunLoading || !dryRunSessionId}
              data-testid="run-dryrun-btn"
            >
              {dryRunLoading ? 'Running…' : 'Run Dry-run'}
            </Button>
          </div>

          {dryRun && (
            <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
              <DryRunPanel title="Flag OFF (current)" payload={dryRun.flag_off} />
              <DryRunPanel title="Flag ON (preview)" payload={dryRun.flag_on} />
              <Card className="md:col-span-2">
                <CardHeader className="pb-2"><CardTitle className="text-base">Deltas (Flag ON − Flag OFF)</CardTitle></CardHeader>
                <CardContent className="text-sm">
                  <table className="w-full">
                    <tbody>
                      {Object.entries(dryRun.deltas || {}).map(([k, v]) => (
                        <tr key={k}>
                          <td className="py-1 font-mono text-xs">{k}</td>
                          <td className="py-1 text-right font-mono">{Number(v).toFixed(4)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <p className="mt-3 text-xs text-muted-foreground">{dryRun.note}</p>
                </CardContent>
              </Card>
            </div>
          )}
        </CardContent>
      </Card>

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-600" />
              Confirm: {pendingValue ? 'Enable' : 'Disable'} Recipe Expansion
            </AlertDialogTitle>
            <AlertDialogDescription className="text-sm">
              {pendingValue ? (
                <>
                  Live stocktake calculations will switch to recipe-aware
                  mode. Sales of products mapped as a recipe output will
                  deduct from <strong>ingredients</strong>, not the parent
                  product. This changes COS attribution and may produce
                  different sales/closing values from existing snapshots.
                </>
              ) : (
                <>
                  Live stocktake calculations will revert to the standard
                  measure engine (no recipe expansion). Existing snapshot
                  rows generated with recipe expansion remain on file but
                  new calculations will not deduct ingredients.
                </>
              )}
              <br /><br />
              The change is logged in the audit log.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="confirm-cancel-btn">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmToggle} data-testid="confirm-toggle-btn">
              Yes, {pendingValue ? 'Enable' : 'Disable'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function DryRunPanel({ title, payload }) {
  if (!payload) return null;
  return (
    <Card>
      <CardHeader className="pb-2"><CardTitle className="text-base">{title}</CardTitle></CardHeader>
      <CardContent className="text-sm space-y-1">
        <div className="font-mono text-xs"><span className="text-muted-foreground">engine_version:</span> {payload.engine_version}</div>
        <div className="font-mono text-xs"><span className="text-muted-foreground">recipe_expansion_enabled:</span> {String(payload.recipe_expansion_enabled)}</div>
        <table className="w-full text-xs font-mono mt-2">
          <tbody>
            <tr><td>opening</td><td className="text-right">{Number(payload.opening_stock_value || 0).toFixed(2)}</td></tr>
            <tr><td>deliveries</td><td className="text-right">{Number(payload.deliveries_value || 0).toFixed(2)}</td></tr>
            <tr><td>credits</td><td className="text-right">{Number(payload.credits_value || 0).toFixed(2)}</td></tr>
            <tr><td>sales</td><td className="text-right">{Number(payload.sales_value || 0).toFixed(2)}</td></tr>
            <tr className="font-bold"><td>closing</td><td className="text-right">{Number(payload.total_value || 0).toFixed(2)}</td></tr>
          </tbody>
        </table>
        {payload.recipe_lines?.length > 0 && (
          <details className="mt-2">
            <summary className="cursor-pointer text-xs text-muted-foreground">
              Recipe-expanded lines ({payload.recipe_lines.length})
            </summary>
            <ul className="mt-1 text-xs">
              {payload.recipe_lines.map((l, i) => (
                <li key={i} className="font-mono py-0.5">
                  pid={l.beverage_product_id} parent_recipe={l.parent_recipe_id} src={l.conversion_source}
                </li>
              ))}
            </ul>
          </details>
        )}
        {payload.calculation_warnings?.length > 0 && (
          <details className="mt-2">
            <summary className="cursor-pointer text-xs text-amber-600">
              Warnings ({payload.calculation_warnings.length})
            </summary>
            <ul className="mt-1 text-xs">
              {payload.calculation_warnings.slice(0, 5).map((w, i) => (
                <li key={i} className="py-0.5"><span className="font-mono">{w.code}</span>: {w.message?.slice(0, 80)}…</li>
              ))}
            </ul>
          </details>
        )}
      </CardContent>
    </Card>
  );
}

/* ─────────────────────── Recipes list ─────────────────────── */

function RecipesTab({ isAdmin }) {
  const [recipes, setRecipes] = useState(null);
  const [error, setError] = useState(null);
  const [openOverridesFor, setOpenOverridesFor] = useState(null);
  const [openEditFor, setOpenEditFor] = useState(null);  // iter356
  // iter433 — quick "Add Recipe" path that reuses EditRecipeDialog in
  // create-mode so admins can hand-create a recipe without needing an
  // .xlsx upload + import-approval round-trip.
  const [openCreate, setOpenCreate] = useState(false);

  const reloadRecipes = () => api.get('/recipes').then(r => setRecipes(r.data));

  useEffect(() => {
    reloadRecipes().catch(e => setError(e?.response?.data?.detail || e.message));
  }, []);

  if (error) return <Alert variant="destructive" className="mt-4"><AlertDescription>{error}</AlertDescription></Alert>;
  if (recipes === null) return <p className="mt-4 text-sm text-muted-foreground">Loading…</p>;

  return (
    <div className="mt-4" data-testid="recipes-list">
      <div className="flex items-center justify-between mb-3">
        <p className="text-xs text-muted-foreground">
          {recipes.length} recipe{recipes.length !== 1 ? 's' : ''} approved.
          Add new ones by clicking <strong>Add Recipe</strong>, or upload
          a post-mix Excel via the Imports tab.
        </p>
        <Button
          size="sm"
          onClick={() => setOpenCreate(true)}
          disabled={!isAdmin}
          data-testid="add-recipe-btn"
        >
          + Add Recipe
        </Button>
      </div>
      {recipes.length === 0 ? (
        <Alert>
          <AlertDescription>
            No recipes yet. Upload a post-mix Excel via the Imports tab,
            review the auto-mapped suggestions, and approve to materialise
            recipes here.
          </AlertDescription>
        </Alert>
      ) : (
        <Card>
          <CardContent className="p-0">
            <table className="w-full">
              <thead className="border-b text-xs text-muted-foreground">
                <tr>
                  <th className="text-left p-3">Name</th>
                  <th className="text-left p-3">Type</th>
                  <th className="text-left p-3">Output Product</th>
                  <th className="text-left p-3">Default Serving</th>
                  <th className="text-left p-3">Active</th>
                  <th className="text-left p-3">Ingredients</th>
                  <th className="text-left p-3">Site Overrides</th>
                  <th className="p-3"></th>
                </tr>
              </thead>
              <tbody>
                {recipes.map(r => {
                  const overrideCount = r.site_overrides?.length || 0;
                  return (
                    <tr key={r.id} className="border-b hover:bg-muted/30" data-testid={`recipe-row-${r.id}`}>
                      <td className="p-3 text-sm font-medium">{r.recipe_name}</td>
                      <td className="p-3 text-sm"><Badge variant="outline">{r.recipe_type}</Badge></td>
                      <td className="p-3 text-sm">
                        {r.output_product_id ? (
                          <div className="flex flex-col leading-tight">
                            <span className="font-medium text-slate-900">
                              {r.output_product_code || '—'}
                            </span>
                            <span className="text-xs text-muted-foreground">
                              {r.output_product_name || `Product #${r.output_product_id}`}
                            </span>
                          </div>
                        ) : (
                          <span className="text-muted-foreground italic">unmapped</span>
                        )}
                      </td>
                      <td className="p-3 text-sm font-mono text-xs">{r.default_serving_label} ({r.default_serving_ml} ml)</td>
                      <td className="p-3 text-sm">{r.is_active ? 'Yes' : 'No'}</td>
                      <td className="p-3 text-sm">
                        {/* iter360d — surface the FIRST stockable ingredient's
                            product code + name so the auditor can verify the
                            recipe is wired to the correct syrup BIB without
                            opening the editor. */}
                        {(() => {
                          const ing = (r.ingredients || []).find(
                            i => i.ingredient_product_id && !i.is_non_stock
                          );
                          if (!ing) {
                            return <span className="text-muted-foreground">{(r.ingredients || []).length}</span>;
                          }
                          return (
                            <div className="flex flex-col leading-tight">
                              <span className="font-medium text-slate-900">
                                {ing.ingredient_product_code || `#${ing.ingredient_product_id}`}
                              </span>
                              <span className="text-xs text-muted-foreground">
                                {ing.ingredient_product_name || ing.ingredient_label || ''}
                              </span>
                            </div>
                          );
                        })()}
                      </td>
                      <td className="p-3 text-sm">
                        {overrideCount > 0
                          ? <Badge data-testid={`recipe-override-count-${r.id}`}>{overrideCount} site{overrideCount !== 1 ? 's' : ''}</Badge>
                          : <span className="text-muted-foreground text-xs italic">defaults</span>}
                      </td>
                      <td className="p-3 text-right">
                        <div className="flex gap-2 justify-end">
                          <Button
                            size="sm"
                            onClick={() => setOpenEditFor(r)}
                            disabled={!isAdmin}
                            data-testid={`edit-recipe-btn-${r.id}`}
                          >
                            Edit recipe
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setOpenOverridesFor(r)}
                            data-testid={`manage-overrides-btn-${r.id}`}
                          >
                            Site Overrides
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}
      {openOverridesFor && (
        <SiteOverridesDialog
          recipe={openOverridesFor}
          isAdmin={isAdmin}
          onClose={(reload) => {
            setOpenOverridesFor(null);
            if (reload) {
              api.get('/recipes').then(r => setRecipes(r.data));
            }
          }}
        />
      )}
      {openEditFor && (
        <EditRecipeDialog
          recipe={openEditFor}
          isAdmin={isAdmin}
          onClose={(reload) => {
            setOpenEditFor(null);
            if (reload) reloadRecipes();
          }}
        />
      )}
      {openCreate && (
        <EditRecipeDialog
          recipe={null}
          isAdmin={isAdmin}
          onClose={(reload) => {
            setOpenCreate(false);
            if (reload) reloadRecipes();
          }}
        />
      )}
    </div>
  );
}

/* ─────────────────────── Site Overrides Dialog ─────────────────────── */

function SiteOverridesDialog({ recipe, isAdmin, onClose }) {
  const [overrides, setOverrides] = useState(null);
  const [sites, setSites] = useState([]);
  const [busy, setBusy] = useState(false);
  // Form state for new / edit override
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({
    site_id: '', carb_water_parts: '', syrup_parts: '',
    default_serving_ml: '', selling_price_incl_vat: '',
    is_active: true, notes: '',
  });
  const [formError, setFormError] = useState(null);

  const reload = () =>
    api.get(`/recipes/${recipe.id}/site-overrides`).then(r => setOverrides(r.data));

  useEffect(() => {
    reload();
    api.get('/sites?page_size=1000').then(r => {
      const items = Array.isArray(r.data) ? r.data : (r.data.items || r.data.results || []);
      setSites(items.map(s => ({ id: s.id, name: s.name })));
    }).catch(() => setSites([]));
  }, [recipe.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const resetForm = () => {
    setEditingId(null);
    setForm({
      site_id: '', carb_water_parts: '', syrup_parts: '',
      default_serving_ml: '', selling_price_incl_vat: '',
      is_active: true, notes: '',
    });
    setFormError(null);
  };

  const beginEdit = (o) => {
    setEditingId(o.id);
    setForm({
      site_id: String(o.site_id),
      carb_water_parts: o.carb_water_parts ?? '',
      syrup_parts: o.syrup_parts ?? '',
      default_serving_ml: o.default_serving_ml ?? '',
      selling_price_incl_vat: o.selling_price_incl_vat ?? '',
      is_active: !!o.is_active,
      notes: o.notes ?? '',
    });
    setFormError(null);
  };

  // Client-side guard rails so we never even POST an obviously bad payload.
  const validate = () => {
    if (!form.site_id) return 'Pick a site';
    const numeric = ['carb_water_parts', 'syrup_parts', 'default_serving_ml', 'selling_price_incl_vat'];
    let anySet = false;
    for (const k of numeric) {
      if (form[k] === '' || form[k] === null) continue;
      anySet = true;
      const n = Number(form[k]);
      if (!Number.isFinite(n)) return `${k} must be a number`;
      if (k === 'selling_price_incl_vat') { if (n < 0) return 'Selling price must be ≥ 0'; }
      else if (n <= 0) return `${k} must be > 0`;
      if (k === 'default_serving_ml' && n > 5000) return 'default_serving_ml looks unrealistic (>5000 ml)';
      if ((k === 'carb_water_parts' || k === 'syrup_parts') && n > 1000) return `${k} looks unrealistic (>1000)`;
    }
    if (!anySet && !form.notes) return 'Set at least one field or add a note';
    return null;
  };

  const save = async () => {
    const err = validate();
    if (err) { setFormError(err); return; }
    setFormError(null);
    setBusy(true);
    try {
      const payload = {
        site_id: parseInt(form.site_id, 10),
        carb_water_parts: form.carb_water_parts === '' ? null : Number(form.carb_water_parts),
        syrup_parts: form.syrup_parts === '' ? null : Number(form.syrup_parts),
        default_serving_ml: form.default_serving_ml === '' ? null : Number(form.default_serving_ml),
        selling_price_incl_vat: form.selling_price_incl_vat === '' ? null : Number(form.selling_price_incl_vat),
        is_active: !!form.is_active,
        notes: form.notes || null,
      };
      await api.post(`/recipes/${recipe.id}/site-overrides`, payload);
      toast.success(editingId ? 'Override updated' : 'Override added');
      resetForm();
      await reload();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = typeof detail === 'string'
        ? detail
        : (Array.isArray(detail) ? detail.map(x => x.msg || x).join('; ') : (e.message || 'Save failed'));
      setFormError(msg);
    } finally {
      setBusy(false);
    }
  };

  const removeOverride = async (siteId) => {
    if (!window.confirm('Delete this site override? The site will revert to recipe defaults.')) return;
    setBusy(true);
    try {
      await api.delete(`/recipes/${recipe.id}/site-overrides/${siteId}`);
      toast.success('Override removed');
      await reload();
    } catch (e) {
      toast.error(`Delete failed: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-testid="site-overrides-dialog">
      <div className="bg-white rounded-lg shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="border-b px-5 py-4 flex items-center justify-between sticky top-0 bg-white">
          <div>
            <h2 className="text-lg font-bold">Site Overrides — {recipe.recipe_name}</h2>
            <p className="text-xs text-muted-foreground">
              Per-site deviations from the global recipe. NULL fields fall
              back to defaults: <span className="font-mono">
                serving={recipe.default_serving_ml}ml · carb={recipe.carb_water_parts ?? '—'}
                {' · '}syrup={recipe.syrup_parts ?? '—'} · price=£{recipe.selling_price_incl_vat ?? '—'}
              </span>
            </p>
          </div>
          <Button variant="ghost" size="sm" onClick={() => onClose(false)} data-testid="close-overrides-btn">Close</Button>
        </div>

        <div className="p-5 space-y-4">
          {/* Existing overrides */}
          <div>
            <h3 className="text-sm font-semibold mb-2">Existing overrides</h3>
            {overrides === null ? (
              <p className="text-xs text-muted-foreground">Loading…</p>
            ) : overrides.length === 0 ? (
              <p className="text-xs text-muted-foreground italic" data-testid="no-overrides-msg">
                No site overrides — every site uses the global recipe defaults.
              </p>
            ) : (
              <table className="w-full text-xs border" data-testid="overrides-table">
                <thead className="bg-muted/30">
                  <tr>
                    <th className="text-left p-2">Site</th>
                    <th className="text-left p-2">Carb : Syrup</th>
                    <th className="text-left p-2">Serving (ml)</th>
                    <th className="text-left p-2">Price £</th>
                    <th className="text-left p-2">Active</th>
                    <th className="text-left p-2">Notes</th>
                    <th className="p-2 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {overrides.map(o => {
                    const siteName = sites.find(s => s.id === o.site_id)?.name || `Site #${o.site_id}`;
                    return (
                      <tr key={o.id} className="border-t" data-testid={`override-row-${o.id}`}>
                        <td className="p-2 font-medium">{siteName}</td>
                        <td className="p-2 font-mono">{o.carb_water_parts ?? '—'} : {o.syrup_parts ?? '—'}</td>
                        <td className="p-2 font-mono">{o.default_serving_ml ?? '—'}</td>
                        <td className="p-2 font-mono">{o.selling_price_incl_vat ?? '—'}</td>
                        <td className="p-2">
                          <Badge variant={o.is_active ? 'default' : 'secondary'}>{o.is_active ? 'Active' : 'Inactive'}</Badge>
                        </td>
                        <td className="p-2 max-w-[160px] truncate">{o.notes || ''}</td>
                        <td className="p-2 text-right space-x-1 whitespace-nowrap">
                          <Button size="sm" variant="outline" disabled={!isAdmin} onClick={() => beginEdit(o)} data-testid={`edit-override-${o.id}`}>Edit</Button>
                          <Button size="sm" variant="destructive" disabled={!isAdmin || busy} onClick={() => removeOverride(o.site_id)} data-testid={`delete-override-${o.id}`}>Delete</Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>

          {/* Add/Edit form */}
          <div className="border-t pt-4">
            <h3 className="text-sm font-semibold mb-2">{editingId ? 'Edit override' : 'Add new site override'}</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              <div>
                <Label htmlFor="ovr-site">Site*</Label>
                <select
                  id="ovr-site"
                  className="w-full border rounded-md px-2 py-1 text-sm"
                  value={form.site_id}
                  onChange={e => setForm({ ...form, site_id: e.target.value })}
                  disabled={!!editingId}
                  data-testid="override-site-select"
                >
                  <option value="">— pick a site —</option>
                  {sites.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
              </div>
              <div>
                <Label htmlFor="ovr-carb">Carb water parts</Label>
                <Input id="ovr-carb" type="number" step="0.01" min="0"
                  value={form.carb_water_parts} onChange={e => setForm({ ...form, carb_water_parts: e.target.value })}
                  data-testid="override-carb-input" />
              </div>
              <div>
                <Label htmlFor="ovr-syrup">Syrup parts</Label>
                <Input id="ovr-syrup" type="number" step="0.01" min="0"
                  value={form.syrup_parts} onChange={e => setForm({ ...form, syrup_parts: e.target.value })}
                  data-testid="override-syrup-input" />
              </div>
              <div>
                <Label htmlFor="ovr-serving">Serving size (ml)</Label>
                <Input id="ovr-serving" type="number" step="0.01" min="0"
                  value={form.default_serving_ml} onChange={e => setForm({ ...form, default_serving_ml: e.target.value })}
                  data-testid="override-serving-input" />
              </div>
              <div>
                <Label htmlFor="ovr-price">Selling price (£ incl VAT)</Label>
                <Input id="ovr-price" type="number" step="0.01" min="0"
                  value={form.selling_price_incl_vat} onChange={e => setForm({ ...form, selling_price_incl_vat: e.target.value })}
                  data-testid="override-price-input" />
              </div>
              <div className="flex items-end gap-2">
                <label className="flex items-center gap-2 text-sm" data-testid="override-active-label">
                  <input type="checkbox" checked={!!form.is_active}
                    onChange={e => setForm({ ...form, is_active: e.target.checked })}
                    data-testid="override-active-checkbox" />
                  Active
                </label>
              </div>
              <div className="sm:col-span-2 lg:col-span-3">
                <Label htmlFor="ovr-notes">Notes</Label>
                <Textarea id="ovr-notes" rows={2} value={form.notes}
                  onChange={e => setForm({ ...form, notes: e.target.value })}
                  data-testid="override-notes-input" />
              </div>
            </div>
            {formError && (
              <Alert variant="destructive" className="mt-3" data-testid="override-form-error">
                <AlertDescription className="text-xs">{formError}</AlertDescription>
              </Alert>
            )}
            <div className="mt-3 flex gap-2">
              <Button onClick={save} disabled={!isAdmin || busy} data-testid="save-override-btn">
                {busy ? 'Saving…' : (editingId ? 'Update Override' : 'Add Override')}
              </Button>
              {editingId && (
                <Button variant="ghost" onClick={resetForm} data-testid="cancel-edit-override-btn">Cancel</Button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────── Pilot Sites tab ─────────────────────── */

function PilotSitesTab({ isAdmin }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [filter, setFilter] = useState('');

  const reload = () => api.get('/recipes/sites-pilot-flags').then(r => setData(r.data));

  useEffect(() => { reload(); }, []);

  const setFlag = async (siteId, enabled) => {
    setBusy(true);
    try {
      await api.patch(`/recipes/sites/${siteId}/pilot-flag`, { enabled });
      toast.success('Pilot flag updated');
      await reload();
    } catch (e) {
      toast.error(`Update failed: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setBusy(false);
    }
  };

  if (data === null) return <p className="mt-4 text-sm text-muted-foreground">Loading…</p>;
  const orgDefault = data.org_default_enabled;
  const sites = (data.sites || []).filter(s =>
    !filter || s.site_name.toLowerCase().includes(filter.toLowerCase())
  );
  const overrideCount = (data.sites || []).filter(s => s.site_override !== null).length;

  return (
    <div className="space-y-4 mt-4" data-testid="pilot-sites-panel">
      <Alert className={orgDefault ? 'border-amber-300 bg-amber-50' : ''}>
        <ShieldAlert className="h-4 w-4" />
        <AlertTitle>Site-level pilot enablement</AlertTitle>
        <AlertDescription className="text-sm">
          <strong>Organisation default:</strong>{' '}
          <Badge variant={orgDefault ? 'default' : 'secondary'} data-testid="pilot-sites-org-default-badge">
            {orgDefault ? 'ON' : 'OFF'}
          </Badge>
          {' · '}
          <strong>{overrideCount}</strong> site{overrideCount !== 1 ? 's have' : ' has'} an override.
          <br />
          <span className="text-xs">
            Each site can <em>inherit</em> the org default, or <em>override</em> it
            with ON / OFF. The change applies to <strong>future calculations only</strong>;
            existing locked snapshots are not mutated. Use this to pilot
            recipe expansion at a single site, or to carve a sensitive
            site out of an org-wide rollout.
          </span>
        </AlertDescription>
      </Alert>

      <div className="flex items-center justify-between gap-3">
        <Input
          placeholder="Filter sites…"
          value={filter}
          onChange={e => setFilter(e.target.value)}
          className="max-w-xs"
          data-testid="pilot-sites-filter"
        />
        <span className="text-xs text-muted-foreground">
          {sites.length} of {data.sites.length} site{data.sites.length !== 1 ? 's' : ''}
        </span>
      </div>

      <Card>
        <CardContent className="p-0">
          <table className="w-full text-sm" data-testid="pilot-sites-table">
            <thead className="border-b text-xs text-muted-foreground bg-muted/20">
              <tr>
                <th className="text-left p-3">Site</th>
                <th className="text-left p-3">Active</th>
                <th className="text-left p-3">Effective</th>
                <th className="text-left p-3">Source</th>
                <th className="text-right p-3">Set override</th>
              </tr>
            </thead>
            <tbody>
              {sites.map(s => (
                <tr key={s.site_id} className="border-b hover:bg-muted/20" data-testid={`pilot-site-row-${s.site_id}`}>
                  <td className="p-3 font-medium">{s.site_name}</td>
                  <td className="p-3"><Badge variant={s.is_active ? 'outline' : 'secondary'}>{s.is_active ? 'Active' : 'Inactive'}</Badge></td>
                  <td className="p-3">
                    <Badge variant={s.effective_enabled ? 'default' : 'secondary'} data-testid={`pilot-site-effective-${s.site_id}`}>
                      {s.effective_enabled ? 'ON' : 'OFF'}
                    </Badge>
                  </td>
                  <td className="p-3 text-xs text-muted-foreground">
                    {s.inherited_from_org
                      ? <span data-testid={`pilot-site-source-${s.site_id}`}>Inherits org default</span>
                      : <span className="font-medium text-purple-700" data-testid={`pilot-site-source-${s.site_id}`}>Site override ({s.site_override ? 'ON' : 'OFF'})</span>}
                  </td>
                  <td className="p-3 text-right space-x-1 whitespace-nowrap">
                    <Button size="sm" variant={s.site_override === true ? 'default' : 'outline'}
                      disabled={!isAdmin || busy}
                      onClick={() => setFlag(s.site_id, true)}
                      data-testid={`pilot-site-set-on-${s.site_id}`}
                    >ON</Button>
                    <Button size="sm" variant={s.site_override === false ? 'destructive' : 'outline'}
                      disabled={!isAdmin || busy}
                      onClick={() => setFlag(s.site_id, false)}
                      data-testid={`pilot-site-set-off-${s.site_id}`}
                    >OFF</Button>
                    <Button size="sm" variant={s.site_override === null ? 'secondary' : 'ghost'}
                      disabled={!isAdmin || busy}
                      onClick={() => setFlag(s.site_id, null)}
                      data-testid={`pilot-site-clear-${s.site_id}`}
                    >Inherit</Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
}

/* ─────────────────────── Imports tab ─────────────────────── */

function ImportsTab({ isAdmin }) {
  const [previews, setPreviews] = useState(null);
  const [selected, setSelected] = useState(null);
  const [busy, setBusy] = useState(false);
  const [file, setFile] = useState(null);
  // Phase 3 Step 5d — post-mix tracks syrup stock only; the till PLU
  // (output product) is metadata. Hide its column from the review grid
  // by default. Admins can reveal it via the toggle if they want to
  // wire recipe expansion to till sales for a specific recipe.
  const [showOutput, setShowOutput] = useState(false);

  const reload = () => api.get('/recipes/import/previews').then(r => setPreviews(r.data));

  useEffect(() => { reload(); }, []);

  const upload = async () => {
    if (!file) { toast.error('Choose an .xlsx file'); return; }
    const fd = new FormData();
    fd.append('file', file);
    setBusy(true);
    try {
      const r = await api.post('/recipes/import/preview', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success(`Parsed ${r.data.rows_parsed} rows → preview #${r.data.preview_id}`);
      setFile(null);
      await reload();
    } catch (e) {
      toast.error(`Upload failed: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setBusy(false);
    }
  };

  const openDetail = async (id) => {
    try {
      const r = await api.get(`/recipes/import/previews/${id}`);
      setSelected(r.data);
    } catch (e) {
      toast.error(`Failed to load preview: ${e.message}`);
    }
  };

  const autoMap = async () => {
    if (!selected) return;
    setBusy(true);
    try {
      const r = await api.post(`/recipes/import/previews/${selected.id}/auto-map`, { min_confidence: 0.55 });
      toast.success(`Auto-map suggested ${r.data.suggestions_committed} matches (review before approving)`);
      await openDetail(selected.id);
    } catch (e) {
      toast.error(`Auto-map failed: ${e.message}`);
    } finally {
      setBusy(false);
    }
  };

  // Phase 3 Step 5b — Bulk-accept ≥0.85-confidence suggestions as DRAFT
  // mappings. Preview stays in pending_review; admin must still click
  // Approve. We deliberately reject auto-approval per spec.
  const acceptHighConf = async () => {
    if (!selected) return;
    if (!window.confirm(
      'Accept all high-confidence (≥85%) auto-mapped suggestions as DRAFT mappings?\n\n' +
      'These are NOT approved — you will still need to review and click Approve afterwards. ' +
      'Lower-confidence rows will remain unmapped for manual review.'
    )) return;
    setBusy(true);
    try {
      const r = await api.post(`/recipes/import/previews/${selected.id}/accept-suggestions`, { min_confidence: 0.85 });
      const out = r.data.accepted_output_mappings || 0;
      const ing = r.data.accepted_ingredient_mappings || 0;
      toast.success(`Accepted ${out} output + ${ing} ingredient mapping(s) as DRAFT — preview is still pending review.`);
      await openDetail(selected.id);
    } catch (e) {
      toast.error(`Bulk accept failed: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setBusy(false);
    }
  };

  // Phase 3 Step 5e — One-click "do everything sensible" flow: bulk-
  // accept any auto-map suggestion that's ≥85% confident, then
  // immediately approve every row that ended up mapped. Unmapped rows
  // are silently skipped. Confirmation dialog explains both steps.
  const acceptAllAndApprove = async () => {
    if (!selected) return;
    if (!window.confirm(
      'Run a one-click "Accept high-confidence + Approve" pass?\n\n' +
      '1) Bulk-copy every ≥85%-confidence auto-map suggestion into the real mapping fields.\n' +
      '2) Approve every row that ends up mapped. Unmapped rows are SILENTLY SKIPPED.\n' +
      '3) Recipes can be deactivated individually afterwards but the import cannot be undone in bulk.\n\n' +
      'Proceed?'
    )) return;
    setBusy(true);
    try {
      const acc = await api.post(`/recipes/import/previews/${selected.id}/accept-suggestions`, { min_confidence: 0.85 });
      const accOut = acc.data.accepted_output_mappings || 0;
      const accIng = acc.data.accepted_ingredient_mappings || 0;
      const r = await api.post(`/recipes/import/previews/${selected.id}/approve`, { require_full_mapping: false });
      const made = (r.data.created_recipes || []).length;
      const skipped = (r.data.skipped_rows || []).length;
      toast.success(`Accepted ${accOut} output + ${accIng} ingredient suggestions → Approved ${made} recipe(s), ${skipped} skipped`);
      setSelected(null);
      await reload();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = typeof detail === 'string'
        ? detail
        : (Array.isArray(detail) ? detail.map(x => x.msg || x).join('; ') : (e.message || 'Failed'));
      toast.error(`Accept & Approve failed: ${msg}`);
    } finally {
      setBusy(false);
    }
  };

  const approve = async () => {
    if (!selected) return;
    const n = selected.row_status_counts?.approvable || 0;
    if (!window.confirm(
      `Approve ${n} mapped row(s)?\n\nUnmapped rows will be silently skipped — you can re-import to address them later. ` +
      `Recipes can be deactivated individually afterwards but the import itself cannot be undone.`
    )) return;
    setBusy(true);
    try {
      // Phase 3 Step 5e — always send require_full_mapping=false so the
      // backend silently skips unmapped rows. The UI already enforces
      // "approvable = mapped + warning" via the dynamic button label.
      const r = await api.post(`/recipes/import/previews/${selected.id}/approve`, { require_full_mapping: false });
      const made = (r.data.created_recipes || []).length;
      const skipped = (r.data.skipped_rows || []).length;
      const replaced = (r.data.replaced_recipe_ids || []).length;
      const dupBlocked = (r.data.duplicates_blocked || []).length;
      const extras = [];
      if (replaced) extras.push(`${replaced} replaced`);
      if (dupBlocked) extras.push(`${dupBlocked} duplicate(s) auto-skipped`);
      toast.success(`Approved — ${made} recipe(s) created, ${skipped} row(s) skipped${extras.length ? ` (${extras.join(', ')})` : ''}`);
      setSelected(null);
      await reload();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = typeof detail === 'string'
        ? detail
        : (Array.isArray(detail) ? detail.map(x => x.msg || x).join('; ') : (e.message || 'Approve failed'));
      toast.error(`Approve failed: ${msg}`);
    } finally {
      setBusy(false);
    }
  };

  const reject = async () => {
    if (!selected) return;
    setBusy(true);
    try {
      await api.post(`/recipes/import/previews/${selected.id}/reject`, { review_notes: 'Rejected via UI' });
      toast.success('Rejected');
      setSelected(null);
      await reload();
    } catch (e) {
      toast.error(`Reject failed: ${e.message}`);
    } finally {
      setBusy(false);
    }
  };

  // Phase 3 Step 5c — Rejected lifecycle
  const reinstate = async () => {
    if (!selected) return;
    setBusy(true);
    try {
      await api.post(`/recipes/import/previews/${selected.id}/reinstate`);
      toast.success('Preview reinstated to pending_review');
      await openDetail(selected.id);
      await reload();
    } catch (e) {
      toast.error(`Reinstate failed: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setBusy(false);
    }
  };

  const deletePreview = async () => {
    if (!selected) return;
    if (!window.confirm(
      `Permanently delete preview #${selected.id}?\n\n` +
      'This cannot be undone. The original Excel file is not retained.'
    )) return;
    setBusy(true);
    try {
      await api.delete(`/recipes/import/previews/${selected.id}`);
      toast.success(`Preview #${selected.id} deleted`);
      setSelected(null);
      await reload();
    } catch (e) {
      toast.error(`Delete failed: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setBusy(false);
    }
  };

  // Phase 3 Step 5c — Per-row actions
  const patchRow = async (rowIdx, body, successMsg) => {
    if (!selected) return;
    setBusy(true);
    try {
      const r = await api.patch(`/recipes/import/previews/${selected.id}/rows/${rowIdx}`, body);
      // Optimistic: refresh whole preview so counts + statuses are consistent.
      await openDetail(selected.id);
      if (successMsg) toast.success(successMsg);
      return r.data;
    } catch (e) {
      toast.error(`Row update failed: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4 mt-4">
      <Card>
        <CardHeader><CardTitle className="flex items-center gap-2"><Upload className="h-5 w-5" />Upload Post-Mix Excel</CardTitle></CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground mb-3">
            Upload a "Post Mix Ratio Yields" .xlsx. The file is parsed into a
            staged preview — nothing is added to live recipes until an admin
            reviews and approves.
          </p>
          <div className="flex items-center gap-3 flex-wrap">
            <Input
              type="file"
              accept=".xlsx"
              onChange={e => setFile(e.target.files?.[0] || null)}
              data-testid="recipe-xlsx-upload"
              disabled={!isAdmin}
              className="max-w-md"
            />
            <Button onClick={upload} disabled={!isAdmin || !file || busy} data-testid="upload-recipe-xlsx-btn">
              {busy ? 'Uploading…' : 'Upload & Review Recipes'}
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle>Pending Previews</CardTitle></CardHeader>
        <CardContent>
          {previews === null ? (
            <p className="text-sm text-muted-foreground">Loading…</p>
          ) : previews.length === 0 ? (
            <p className="text-sm text-muted-foreground">No previews yet.</p>
          ) : (
            <ul className="space-y-2" data-testid="preview-list">
              {previews.map(p => (
                <li key={p.id}>
                  <button
                    onClick={() => openDetail(p.id)}
                    className="w-full flex items-center justify-between p-3 border rounded hover:bg-muted/30 text-left"
                    data-testid={`preview-row-${p.id}`}
                  >
                    <div>
                      <div className="font-medium text-sm">#{p.id} — {p.import_filename}</div>
                      <div className="text-xs text-muted-foreground">
                        Imported {new Date(p.imported_at).toLocaleString()}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge variant={p.status === 'pending_review' ? 'default' : 'secondary'}>{p.status}</Badge>
                      <ChevronRight className="h-4 w-4 text-muted-foreground" />
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      {selected && (
        <Card data-testid="preview-detail">
          <CardHeader>
            <div className="flex items-center justify-between flex-wrap gap-2">
              <CardTitle>
                Preview #{selected.id} — {selected.mapped_recipes?.length || 0} recipes
              </CardTitle>
              <div className="flex items-center gap-2">
                <Badge variant={selected.status === 'pending_review' ? 'default' : 'secondary'} data-testid="preview-status-badge">
                  {selected.status}
                </Badge>
                <Button size="sm" variant="ghost" onClick={() => setSelected(null)}>Close</Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {/* Status counts band */}
            {selected.row_status_counts && (
              <div className="flex flex-wrap gap-2 mb-3 text-xs" data-testid="status-counts-band">
                <span className="px-2 py-1 rounded bg-emerald-100 text-emerald-800" data-testid="count-mapped">
                  ✓ Mapped: <strong>{selected.row_status_counts.mapped}</strong>
                </span>
                <span className="px-2 py-1 rounded bg-amber-100 text-amber-800" data-testid="count-warning">
                  ⚠ Warning: <strong>{selected.row_status_counts.warning}</strong>
                </span>
                <span className="px-2 py-1 rounded bg-purple-100 text-purple-800" data-testid="count-suggested">
                  ? Suggested: <strong>{selected.row_status_counts.suggested}</strong>
                </span>
                <span className="px-2 py-1 rounded bg-gray-100 text-gray-700" data-testid="count-unmapped">
                  · Unmapped: <strong>{selected.row_status_counts.unmapped}</strong>
                </span>
                <span className="px-2 py-1 rounded bg-slate-200 text-slate-700" data-testid="count-skipped">
                  ⊘ Skipped: <strong>{selected.row_status_counts.skipped}</strong>
                </span>
                {(() => {
                  const dupCount = (selected.mapped_recipes || []).filter(r => r._duplicate_recipe_id).length;
                  return dupCount > 0 && (
                    <span className="px-2 py-1 rounded bg-orange-100 text-orange-800" data-testid="count-duplicates">
                      ⚠ Duplicates: <strong>{dupCount}</strong>
                    </span>
                  );
                })()}
                <span className="ml-auto text-muted-foreground">
                  Total: {selected.row_status_counts.total} ·
                  Approvable now: <strong className="text-emerald-700">{selected.row_status_counts.approvable}</strong>
                </span>
              </div>
            )}

            {/* iter356 — duplicate-name warning banner. Lists each clashing row
                so the admin can act per-row via the "Duplicate of…" picker below. */}
            {(() => {
              const dups = (selected.mapped_recipes || []).filter(r => r._duplicate_recipe_id && !r._duplicate_action);
              if (dups.length === 0) return null;
              return (
                <Alert variant="default" className="mb-3 border-orange-300 bg-orange-50" data-testid="duplicate-warning">
                  <AlertTriangle className="h-4 w-4 text-orange-600" />
                  <AlertTitle className="text-orange-900">
                    {dups.length} row{dups.length === 1 ? '' : 's'} match{dups.length === 1 ? 'es' : ''} an existing recipe name
                  </AlertTitle>
                  <AlertDescription className="text-xs text-orange-900">
                    Rows without an explicit duplicate action are <strong>skipped</strong> on Approve so a duplicate is never created silently.
                    Pick one of <em>Skip / Replace existing / Create as new copy</em> in the row's Actions column to decide what to do.
                  </AlertDescription>
                </Alert>
              );
            })()}

            {selected.parser_warnings?.length > 0 && (
              <Alert variant="destructive" className="mb-3">
                <AlertTitle>Parser warnings ({selected.parser_warnings.length})</AlertTitle>
                <AlertDescription className="text-xs">
                  <ul className="list-disc pl-4">
                    {selected.parser_warnings.slice(0, 6).map((w, i) => <li key={i}>{w}</li>)}
                  </ul>
                </AlertDescription>
              </Alert>
            )}
            {selected.assumptions?.length > 0 && (
              <details className="mb-3">
                <summary className="cursor-pointer text-sm text-muted-foreground">
                  Importer assumptions ({selected.assumptions.length})
                </summary>
                <ul className="text-xs list-disc pl-4 mt-2 space-y-1">
                  {selected.assumptions.map((a, i) => <li key={i}>{a}</li>)}
                </ul>
              </details>
            )}

            {/* Action bar — different controls per preview status */}
            <div className="flex gap-2 mb-4 flex-wrap items-center">
              <label className="text-xs text-muted-foreground flex items-center gap-1" data-testid="show-output-toggle-label">
                <input
                  type="checkbox"
                  checked={showOutput}
                  onChange={e => setShowOutput(e.target.checked)}
                  data-testid="show-output-toggle"
                />
                Show Output (till PLU) column
              </label>
              {selected.status === 'pending_review' && (
                <>
                  <Button size="sm" variant="outline" onClick={autoMap} disabled={!isAdmin || busy} data-testid="auto-map-btn">
                    Auto-map suggestions
                  </Button>
                  <Button size="sm" variant="outline" onClick={acceptHighConf} disabled={!isAdmin || busy} data-testid="accept-suggestions-btn">
                    Accept high-confidence (≥85%) as Draft
                  </Button>
                  <div className="ml-auto flex gap-2">
                    <Button
                      size="sm"
                      variant="secondary"
                      onClick={acceptAllAndApprove}
                      disabled={!isAdmin || busy}
                      data-testid="accept-and-approve-btn"
                      title="Accept every ≥85%-confidence suggestion AND approve all resulting mapped rows in one go. Unmapped rows are silently skipped."
                    >
                      Accept & Approve all
                    </Button>
                    <Button
                      size="sm"
                      onClick={approve}
                      disabled={!isAdmin || busy || !selected.row_status_counts?.approvable}
                      data-testid="approve-preview-btn"
                      title={
                        selected.row_status_counts?.approvable
                          ? `Approve ${selected.row_status_counts.approvable} mapped row(s); unmapped/skipped rows are ignored.`
                          : 'No rows are fully mapped yet — map at least one row before approving.'
                      }
                    >
                      Approve {selected.row_status_counts?.approvable || 0} row(s)
                    </Button>
                    <Button size="sm" variant="destructive" onClick={reject} disabled={!isAdmin || busy} data-testid="reject-preview-btn">
                      Reject preview
                    </Button>
                  </div>
                </>
              )}
              {selected.status === 'rejected' && (
                <>
                  <Button size="sm" onClick={reinstate} disabled={!isAdmin || busy} data-testid="reinstate-preview-btn">
                    Reinstate to pending review
                  </Button>
                  <Button size="sm" variant="destructive" onClick={deletePreview} disabled={!isAdmin || busy} data-testid="delete-preview-btn">
                    Delete permanently
                  </Button>
                </>
              )}
              {selected.status === 'approved' && (
                <span className="text-xs text-muted-foreground italic">
                  This preview has been approved and materialised — no further actions.
                </span>
              )}
            </div>

            {/* Per-row review grid */}
            <div className="overflow-x-auto border rounded">
              <table className="w-full text-xs" data-testid="preview-mapping-table">
                <thead className="bg-muted/30 border-b">
                  <tr>
                    <th className="text-left p-2 sticky left-0 bg-muted/30 z-10">Status</th>
                    <th className="text-left p-2">Drink</th>
                    <th className="text-right p-2">Carb</th>
                    <th className="text-right p-2">Syrup</th>
                    <th className="text-right p-2">BIB L</th>
                    <th className="text-left p-2">Glass</th>
                    <th className="text-right p-2">Resolved ml</th>
                    <th className="text-right p-2">Syrup ml/serv</th>
                    {showOutput && (
                      <th className="text-left p-2" data-testid="th-output-pid">Output PID (till PLU)</th>
                    )}
                    <th className="text-left p-2">Syrup product (stock-tracked)</th>
                    <th className="text-left p-2">Suggestions</th>
                    <th className="text-left p-2">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {(selected.mapped_recipes || []).map((r) => {
                    const idx = r._row_idx;
                    const status = r._review_status;
                    const syrupIng = (r.ingredients || []).find(i => !i.is_non_stock && (i.role === 'syrup' || true));
                    const isPending = selected.status === 'pending_review';
                    return (
                      <PerRowReview
                        key={idx}
                        r={r}
                        idx={idx}
                        status={status}
                        syrupIng={syrupIng}
                        isPending={isPending}
                        isAdmin={isAdmin}
                        busy={busy}
                        patchRow={patchRow}
                        showOutput={showOutput}
                      />
                    );
                  })}
                </tbody>
              </table>
            </div>

            <p className="text-xs text-muted-foreground mt-3">
              <strong>Tip:</strong> auto-mapping suggests product IDs but never approves them.
              Use "Accept high-confidence (≥85%) as Draft" to bulk-pre-fill the suggestions, then
              review row-by-row before clicking Approve. Use the per-row "Skip" toggle to exclude
              a row from the import without rejecting the whole preview.
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

/* ─────────────────────── Per-row review component ─────────────────────── */

function PerRowReview({ r, idx, status, syrupIng, isPending, isAdmin, busy, patchRow, showOutput }) {
  const [editingOutput, setEditingOutput] = useState(false);
  const [outputDraft, setOutputDraft] = useState(String(r.output_product_id ?? ''));
  const [editingSyrup, setEditingSyrup] = useState(false);

  const STATUS_STYLES = {
    mapped:    { bg: 'bg-emerald-100', text: 'text-emerald-800', icon: '✓' },
    warning:   { bg: 'bg-amber-100',   text: 'text-amber-800',   icon: '⚠' },
    suggested: { bg: 'bg-purple-100',  text: 'text-purple-800',  icon: '?' },
    unmapped:  { bg: 'bg-gray-100',    text: 'text-gray-700',    icon: '·' },
    skipped:   { bg: 'bg-slate-200',   text: 'text-slate-700',   icon: '⊘' },
  };
  const sty = STATUS_STYLES[status] || STATUS_STYLES.unmapped;

  const saveOutput = async () => {
    const val = outputDraft.trim();
    if (val === '') {
      await patchRow(idx, { clear_output_product_id: true }, 'Cleared output product');
    } else {
      const n = parseInt(val, 10);
      if (!Number.isFinite(n) || n <= 0) { toast.error('Enter a positive integer product ID'); return; }
      await patchRow(idx, { output_product_id: n }, `Set output product → ${n}`);
    }
    setEditingOutput(false);
  };

  const pickSyrup = async (pid, name) => {
    const role = syrupIng?.role || syrupIng?.ingredient_label || 'syrup';
    if (pid === null) {
      await patchRow(idx, { ingredient_product_ids: { [role]: null } }, 'Cleared syrup ingredient');
    } else {
      await patchRow(idx, { ingredient_product_ids: { [role]: pid } }, `Syrup → ${name} (#${pid})`);
    }
    setEditingSyrup(false);
  };

  return (
    <tr
      className={`border-b align-top ${status === 'skipped' ? 'opacity-60' : ''}`}
      data-testid={`preview-row-detail-${idx}`}
    >
      <td className={`p-2 sticky left-0 ${sty.bg} z-10`}>
        <span
          className={`px-1.5 py-0.5 rounded font-semibold ${sty.bg} ${sty.text}`}
          data-testid={`row-status-${idx}`}
        >
          {sty.icon} {status}
        </span>
      </td>
      <td className="p-2 font-medium" data-testid={`row-name-${idx}`}>
        {r.recipe_name}
        {r._duplicate_recipe_id && (
          <div className="mt-1">
            <Badge variant="outline" className="text-[10px] border-orange-400 text-orange-800 bg-orange-50"
              data-testid={`row-duplicate-badge-${idx}`}
              title={`Existing recipe #${r._duplicate_recipe_id}: ${r._duplicate_recipe_name}`}
            >
              ⚠ Duplicate of "{r._duplicate_recipe_name}"
            </Badge>
            {r._duplicate_action && (
              <Badge variant="secondary" className="text-[10px] ml-1" data-testid={`row-duplicate-action-badge-${idx}`}>
                Action: {r._duplicate_action.replace('_', ' ')}
              </Badge>
            )}
          </div>
        )}
      </td>
      <td className="p-2 text-right font-mono">{r.carb_water_parts ?? '—'}</td>
      <td className="p-2 text-right font-mono">{r.syrup_parts ?? '—'}</td>
      <td className="p-2 text-right font-mono">{r.syrup_box_litres ?? '—'}</td>
      <td className="p-2">{r.default_serving_label || <span className="text-muted-foreground italic">—</span>}</td>
      <td className="p-2 text-right font-mono" data-testid={`row-default-ml-${idx}`}>
        {r.default_serving_ml ? Number(r.default_serving_ml).toFixed(2) : (
          <span className="text-amber-700 font-semibold">unresolved</span>
        )}
      </td>
      <td className="p-2 text-right font-mono">
        {syrupIng?.ingredient_qty ? Number(syrupIng.ingredient_qty).toFixed(2) : '—'}
      </td>
      {/* Output PID editor (hidden by default) */}
      {showOutput && (
        <td className="p-2" data-testid={`row-output-${idx}`}>
          {editingOutput ? (
            <div className="flex gap-1">
              <input
                type="number"
                className="border rounded px-1 py-0.5 w-20 text-xs"
                value={outputDraft}
                onChange={e => setOutputDraft(e.target.value)}
                data-testid={`row-output-input-${idx}`}
              />
              <Button size="sm" className="h-6 px-2" onClick={saveOutput} disabled={busy} data-testid={`row-output-save-${idx}`}>Save</Button>
              <Button size="sm" variant="ghost" className="h-6 px-2" onClick={() => { setEditingOutput(false); setOutputDraft(String(r.output_product_id ?? '')); }}>Cancel</Button>
            </div>
          ) : (
            <button
              className="text-left underline-offset-2 hover:underline disabled:no-underline disabled:cursor-default"
              disabled={!isPending || !isAdmin}
              onClick={() => setEditingOutput(true)}
              data-testid={`row-output-edit-${idx}`}
            >
              {r.output_product_id ?? <span className="text-muted-foreground italic">unmapped</span>}
            </button>
          )}
        </td>
      )}
      {/* Syrup product picker (typeahead by name) */}
      <td className="p-2" data-testid={`row-syrup-${idx}`}>
        {syrupIng ? (
          editingSyrup ? (
            <ProductTypeahead
              currentName={syrupIng.ingredient_label}
              onPick={(p) => pickSyrup(p.id, p.product_name)}
              onClear={() => pickSyrup(null)}
              onCancel={() => setEditingSyrup(false)}
              testidPrefix={`row-syrup-picker-${idx}`}
            />
          ) : (
            <button
              className="text-left underline-offset-2 hover:underline disabled:no-underline disabled:cursor-default"
              disabled={!isPending || !isAdmin}
              onClick={() => setEditingSyrup(true)}
              data-testid={`row-syrup-edit-${idx}`}
              title={syrupIng.ingredient_product_id ? `Click to change (current PID #${syrupIng.ingredient_product_id})` : 'Click to pick a syrup product'}
            >
              {syrupIng.ingredient_product_id ? (
                <span>
                  <span className="font-medium">{syrupIng.ingredient_product_name || `Product #${syrupIng.ingredient_product_id}`}</span>
                  <span className="text-muted-foreground ml-1">(#{syrupIng.ingredient_product_id})</span>
                </span>
              ) : (
                <span className="text-muted-foreground italic">— pick product —</span>
              )}
            </button>
          )
        ) : (
          <span className="text-muted-foreground italic">no syrup</span>
        )}
      </td>
      {/* Suggestions */}
      <td className="p-2 text-muted-foreground" data-testid={`row-suggestions-${idx}`}>
        <div className="space-y-0.5">
          {r.output_product_id_suggested && showOutput && (
            <div>
              out: <span className="font-mono">{r.output_product_id_suggested}</span>
              {r.output_product_confidence != null && <span> ({r.output_product_confidence.toFixed(2)})</span>}
              {r.output_product_name_suggested && <span> · {r.output_product_name_suggested}</span>}
            </div>
          )}
          {syrupIng?.ingredient_product_id_suggested && (
            <div>
              syr: <span className="font-mono">{syrupIng.ingredient_product_id_suggested}</span>
              {syrupIng.ingredient_product_confidence != null && <span> ({syrupIng.ingredient_product_confidence.toFixed(2)})</span>}
              {syrupIng.ingredient_product_name_suggested && <span> · {syrupIng.ingredient_product_name_suggested}</span>}
            </div>
          )}
          {!syrupIng?.ingredient_product_id_suggested && (!r.output_product_id_suggested || !showOutput) && '—'}
        </div>
      </td>
      {/* Actions */}
      <td className="p-2" data-testid={`row-actions-${idx}`}>
        <div className="flex flex-wrap gap-1">
          {(syrupIng?.ingredient_product_id_suggested || (r.output_product_id_suggested && showOutput)) && (
            <Button
              size="sm" variant="outline" className="h-6 px-2 text-xs"
              disabled={!isPending || !isAdmin || busy || status === 'mapped' || status === 'warning'}
              onClick={() => patchRow(idx, {
                accept_output_suggestion: showOutput,
                accept_ingredient_suggestions: true,
              }, `Row ${idx + 1} suggestions accepted`)}
              data-testid={`row-accept-${idx}`}
            >
              Accept
            </Button>
          )}
          <Button
            size="sm" variant={r._skipped ? 'default' : 'ghost'} className="h-6 px-2 text-xs"
            disabled={!isPending || !isAdmin || busy}
            onClick={() => patchRow(idx, { skipped: !r._skipped },
              r._skipped ? `Row ${idx + 1} restored` : `Row ${idx + 1} skipped`)}
            data-testid={`row-skip-${idx}`}
          >
            {r._skipped ? 'Restore' : 'Skip'}
          </Button>
          {/* iter356 — duplicate action picker (only shown if this row collides). */}
          {r._duplicate_recipe_id && isPending && (
            <select
              className="h-6 text-xs border rounded px-1 bg-orange-50 border-orange-300"
              value={r._duplicate_action || ''}
              disabled={!isAdmin || busy}
              onChange={(e) => patchRow(
                idx,
                { duplicate_action: e.target.value || '' },
                e.target.value
                  ? `Duplicate action → ${e.target.value.replace('_', ' ')}`
                  : 'Cleared duplicate action'
              )}
              data-testid={`row-duplicate-action-${idx}`}
              title="Decide what to do because this row matches an existing recipe name. 'Skip' is the safe default."
            >
              <option value="">Duplicate? Pick action…</option>
              <option value="skip">Skip (default — keep existing)</option>
              <option value="replace">Replace existing (deactivate old)</option>
              <option value="create_new">Create as new copy</option>
            </select>
          )}
        </div>
      </td>
    </tr>
  );
}

/* ─────────────────────── Product typeahead ─────────────────────── */

function ProductTypeahead({ currentName, onPick, onClear, onCancel, testidPrefix }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(true);

  useEffect(() => {
    // Pre-fill the search box with the parser's auto-suggested ingredient
    // label (e.g. 'Coca-Cola Syrup BIB (7L)'). Strip the trailing '(7L)' /
    // 'Syrup BIB' boilerplate so we land on a useful query token.
    const seed = (currentName || '')
      .replace(/\s*syrup bib.*$/i, '')
      .replace(/\s*\(.+?\)\s*$/, '')
      .trim();
    setQuery(seed);
  }, [currentName]);

  useEffect(() => {
    if (!open || !query || query.trim().length < 2) {
      setResults([]);
      return;
    }
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const r = await api.get(`/count/products/search`, {
          params: { q: query.trim(), page_size: 12 },
        });
        setResults(r.data?.items || []);
      } catch (e) {
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 200);
    return () => clearTimeout(t);
  }, [query, open]);

  return (
    <div className="relative w-72" data-testid={testidPrefix}>
      <div className="flex gap-1">
        <input
          type="text"
          autoFocus
          className="border rounded px-2 py-1 text-xs w-full"
          placeholder="Search by product name…"
          value={query}
          onChange={e => setQuery(e.target.value)}
          data-testid={`${testidPrefix}-input`}
        />
        <Button size="sm" variant="ghost" className="h-7 px-2" onClick={onCancel} data-testid={`${testidPrefix}-cancel`}>
          Cancel
        </Button>
        <Button size="sm" variant="destructive" className="h-7 px-2" onClick={onClear} data-testid={`${testidPrefix}-clear`}>
          Clear
        </Button>
      </div>
      {open && query.trim().length >= 2 && (
        <div className="absolute z-20 left-0 right-0 mt-1 border rounded bg-white shadow-lg max-h-72 overflow-y-auto"
             data-testid={`${testidPrefix}-dropdown`}>
          {loading && <div className="p-2 text-xs text-muted-foreground">Searching…</div>}
          {!loading && results.length === 0 && (
            <div className="p-2 text-xs text-muted-foreground">No products match "{query}"</div>
          )}
          {results.map(p => (
            <button
              key={p.id}
              className="block w-full text-left px-2 py-1 text-xs hover:bg-muted/50 border-b last:border-b-0"
              onClick={() => onPick(p)}
              data-testid={`${testidPrefix}-result-${p.id}`}
            >
              <div className="font-medium">{p.product_name}</div>
              <div className="text-muted-foreground">
                #{p.id}
                {p.brand ? ` · ${p.brand}` : ''}
                {p.category ? ` · ${p.category}` : ''}
                {p.case_size ? ` · ${p.case_size}` : ''}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/* ─────────────────────── Edit Recipe Dialog (iter356) ─────────────────────── */

function EditRecipeDialog({ recipe, isAdmin, onClose }) {
  const [busy, setBusy] = useState(false);
  const [full, setFull] = useState(null);
  const [error, setError] = useState(null);
  const [form, setForm] = useState(null);
  // iter433 — create-mode. When `recipe` is null we POST a new recipe
  // instead of PATCHing an existing one; the dialog layout, validation
  // and inputs are identical so we get a free "Add Recipe" UI from the
  // existing edit dialog without duplicating ~250 lines of JSX.
  const isCreate = !recipe;
  // iter381 — load lightweight master product list once so the
  // output-product + each ingredient row gets a searchable picker
  // instead of "type a numeric PID" UX. Without this, recipes whose
  // auto-mapper missed a product (e.g. "R Whites Lemonade Syrup BIB"
  // → "Postmix R-Whites") have NO way to be fixed from the admin UI.
  const [products, setProducts] = useState([]);
  useEffect(() => {
    beverageProductsService.listAllLite()
      .then(list => setProducts(Array.isArray(list) ? list : []))
      .catch(() => setProducts([]));
  }, []);

  // Fetch the full recipe once on open so we always edit the freshest
  // copy. Create-mode skips the fetch and seeds an empty draft.
  useEffect(() => {
    if (isCreate) {
      setFull({});
      setForm({
        recipe_type: 'post_mix',
        recipe_name: '',
        output_product_id: '',
        output_label: '',
        default_serving_label: '',
        default_serving_ml: '',
        carb_water_parts: '',
        syrup_parts: '',
        syrup_box_litres: '',
        waste_factor: '0',
        selling_price_incl_vat: '',
        is_active: true,
        notes: '',
        ingredients: [],
        // Seed one default serving row so the user only has to fill it
        // in, not also click "+ Add" for the most common case.
        servings: [{
          serving_label: '', serving_ml: '', selling_price_incl_vat: '',
          is_default: true,
        }],
      });
      return;
    }
    api.get(`/recipes/${recipe.id}`)
      .then(r => {
        setFull(r.data);
        setForm({
          recipe_type: r.data.recipe_type || 'post_mix',
          recipe_name: r.data.recipe_name || '',
          output_product_id: r.data.output_product_id ?? '',
          output_label: r.data.output_label || '',
          default_serving_label: r.data.default_serving_label || '',
          default_serving_ml: r.data.default_serving_ml ?? '',
          carb_water_parts: r.data.carb_water_parts ?? '',
          syrup_parts: r.data.syrup_parts ?? '',
          syrup_box_litres: r.data.syrup_box_litres ?? '',
          waste_factor: r.data.waste_factor ?? '0',
          selling_price_incl_vat: r.data.selling_price_incl_vat ?? '',
          is_active: r.data.is_active !== false,
          notes: r.data.notes || '',
          ingredients: (r.data.ingredients || []).map(i => ({
            ingredient_product_id: i.ingredient_product_id ?? null,
            ingredient_product_name: i.ingredient_product_name || null,
            ingredient_label: i.ingredient_label || '',
            ingredient_qty: i.ingredient_qty ?? '',
            ingredient_unit: i.ingredient_unit || 'ml',
            is_non_stock: !!i.is_non_stock,
            sort_order: i.sort_order ?? 0,
            notes: i.notes || '',
          })),
          servings: (r.data.servings || []).map(s => ({
            serving_label: s.serving_label || '',
            serving_ml: s.serving_ml ?? '',
            selling_price_incl_vat: s.selling_price_incl_vat ?? '',
            is_default: !!s.is_default,
          })),
        });
      })
      .catch(e => setError(e?.response?.data?.detail || e.message));
  }, [recipe?.id, isCreate]); // eslint-disable-line react-hooks/exhaustive-deps

  const updateForm = (patch) => setForm(f => ({ ...f, ...patch }));

  const updateIngredient = (idx, patch) => setForm(f => {
    const next = [...f.ingredients];
    next[idx] = { ...next[idx], ...patch };
    return { ...f, ingredients: next };
  });
  const addIngredient = () => setForm(f => ({
    ...f,
    ingredients: [
      ...f.ingredients,
      {
        ingredient_product_id: null, ingredient_product_name: null,
        ingredient_label: '', ingredient_qty: '', ingredient_unit: 'ml',
        is_non_stock: false, sort_order: f.ingredients.length, notes: '',
      },
    ],
  }));
  const removeIngredient = (idx) => setForm(f => ({
    ...f, ingredients: f.ingredients.filter((_, i) => i !== idx),
  }));

  const updateServing = (idx, patch) => setForm(f => {
    const next = [...f.servings];
    next[idx] = { ...next[idx], ...patch };
    if (patch.is_default) {
      next.forEach((s, i) => { if (i !== idx) s.is_default = false; });
    }
    return { ...f, servings: next };
  });
  const addServing = () => setForm(f => ({
    ...f,
    servings: [
      ...f.servings,
      { serving_label: '', serving_ml: '', selling_price_incl_vat: '', is_default: f.servings.length === 0 },
    ],
  }));
  const removeServing = (idx) => setForm(f => ({
    ...f, servings: f.servings.filter((_, i) => i !== idx),
  }));

  const save = async () => {
    if (!form) return;
    if (!form.recipe_name.trim()) { toast.error('Recipe name is required'); return; }
    if (!form.servings.length) { toast.error('At least one serving is required'); return; }
    if (form.servings.some(s => !s.serving_label.trim() || !s.serving_ml)) {
      toast.error('Every serving needs a label and ml value');
      return;
    }
    if (!form.servings.some(s => s.is_default)) {
      toast.error('Pick exactly one default serving');
      return;
    }
    if (form.ingredients.some(i => !i.ingredient_qty || !i.ingredient_unit)) {
      toast.error('Every ingredient needs a quantity and unit');
      return;
    }
    setBusy(true);
    let closed = false;
    try {
      const payload = {
        recipe_type: form.recipe_type,
        recipe_name: form.recipe_name.trim(),
        output_product_id: form.output_product_id === '' ? null : Number(form.output_product_id),
        output_label: form.output_label || null,
        default_serving_label: form.default_serving_label || null,
        default_serving_ml: form.default_serving_ml === '' ? null : Number(form.default_serving_ml),
        carb_water_parts: form.carb_water_parts === '' ? null : Number(form.carb_water_parts),
        syrup_parts: form.syrup_parts === '' ? null : Number(form.syrup_parts),
        syrup_box_litres: form.syrup_box_litres === '' ? null : Number(form.syrup_box_litres),
        waste_factor: form.waste_factor === '' ? 0 : Number(form.waste_factor),
        selling_price_incl_vat: form.selling_price_incl_vat === '' ? null : Number(form.selling_price_incl_vat),
        notes: form.notes || null,
        is_active: form.is_active,
        ingredients: form.ingredients.map((i, idx) => ({
          ingredient_product_id: i.ingredient_product_id || null,
          ingredient_label: i.ingredient_label || null,
          ingredient_qty: Number(i.ingredient_qty),
          ingredient_unit: i.ingredient_unit,
          is_non_stock: !!i.is_non_stock,
          sort_order: i.sort_order ?? idx,
          notes: i.notes || null,
        })),
        servings: form.servings.map(s => ({
          serving_label: s.serving_label,
          serving_ml: Number(s.serving_ml),
          selling_price_incl_vat: s.selling_price_incl_vat === '' ? null : Number(s.selling_price_incl_vat),
          is_default: !!s.is_default,
        })),
      };
      if (isCreate) {
        const r = await api.post('/recipes', payload);
        toast.success(`Recipe "${r.data?.recipe_name || form.recipe_name}" created`);
      } else {
        await api.patch(`/recipes/${recipe.id}`, payload);
        toast.success(`Recipe "${form.recipe_name}" saved`);
      }
      // Close BEFORE finally so we don't setState on an unmounted node.
      closed = true;
      onClose(true);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = typeof detail === 'string'
        ? detail
        : (Array.isArray(detail) ? detail.map(x => x.msg || x).join('; ') : (e.message || 'Save failed'));
      toast.error(`Save failed: ${msg}`);
    } finally {
      if (!closed) setBusy(false);
    }
  };

  if (error) {
    return (
      <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
        <div className="bg-white p-6 rounded-md max-w-md">
          <Alert variant="destructive"><AlertDescription>{error}</AlertDescription></Alert>
          <Button className="mt-4" onClick={() => onClose(false)}>Close</Button>
        </div>
      </div>
    );
  }
  if (!full || !form) {
    return (
      <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
        <div className="bg-white p-6 rounded-md">Loading recipe…</div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="edit-recipe-dialog">
      <div className="bg-white rounded-md max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between z-10">
          <div>
            <h2 className="text-lg font-semibold">
              {isCreate ? 'Add new recipe' : `Edit recipe #${recipe.id}`}
            </h2>
            <p className="text-xs text-muted-foreground">
              {isCreate
                ? 'Fill in the fields below — examples in light grey show the expected format. You can leave optional fields blank.'
                : 'Changes apply globally. Site-specific deviations belong in '}
              {!isCreate && <em>Site Overrides</em>}
              {!isCreate && ' — not here.'}
            </p>
          </div>
          <Button variant="ghost" size="sm" onClick={() => onClose(false)}>Close</Button>
        </div>
        <div className="px-6 py-4 space-y-6">

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Recipe name</Label>
              <Input value={form.recipe_name}
                onChange={e => updateForm({ recipe_name: e.target.value })}
                disabled={!isAdmin}
                placeholder="e.g. Coca-Cola Post-Mix 22 oz"
                data-testid="edit-recipe-name" />
            </div>
            <div>
              <Label>Recipe type</Label>
              <select className="w-full border rounded h-9 px-2 text-sm bg-white"
                value={form.recipe_type}
                onChange={e => updateForm({ recipe_type: e.target.value })}
                disabled={!isAdmin} data-testid="edit-recipe-type">
                <option value="post_mix">Post-Mix</option>
                <option value="cocktail">Cocktail</option>
                <option value="batch">Batch</option>
              </select>
            </div>
            <div>
              <Label>Default serving label</Label>
              <Input value={form.default_serving_label}
                onChange={e => updateForm({ default_serving_label: e.target.value })}
                placeholder="e.g. 22 oz" disabled={!isAdmin}
                data-testid="edit-recipe-default-serving-label" />
            </div>
            <div>
              <Label>Default serving (ml)</Label>
              <Input type="number" step="0.01" value={form.default_serving_ml}
                onChange={e => updateForm({ default_serving_ml: e.target.value })}
                disabled={!isAdmin}
                placeholder="e.g. 650"
                data-testid="edit-recipe-default-serving-ml" />
            </div>
            <div>
              <Label>Carb water parts</Label>
              <Input type="number" step="0.01" value={form.carb_water_parts}
                onChange={e => updateForm({ carb_water_parts: e.target.value })}
                disabled={!isAdmin}
                placeholder="e.g. 5"
                data-testid="edit-recipe-carb" />
            </div>
            <div>
              <Label>Syrup parts</Label>
              <Input type="number" step="0.01" value={form.syrup_parts}
                onChange={e => updateForm({ syrup_parts: e.target.value })}
                disabled={!isAdmin}
                placeholder="e.g. 1"
                data-testid="edit-recipe-syrup-parts" />
            </div>
            <div>
              <Label>Syrup box litres (BIB)</Label>
              <Input type="number" step="0.01" value={form.syrup_box_litres}
                onChange={e => updateForm({ syrup_box_litres: e.target.value })}
                disabled={!isAdmin}
                placeholder="e.g. 10"
                data-testid="edit-recipe-syrup-bib" />
            </div>
            <div>
              <Label>Waste factor (0–1)</Label>
              <Input type="number" step="0.001" value={form.waste_factor}
                onChange={e => updateForm({ waste_factor: e.target.value })}
                disabled={!isAdmin}
                placeholder="e.g. 0.05 (5% spillage)"
                data-testid="edit-recipe-waste" />
            </div>
            <div>
              <Label>Selling price (£, incl. VAT)</Label>
              <Input type="number" step="0.01" value={form.selling_price_incl_vat}
                onChange={e => updateForm({ selling_price_incl_vat: e.target.value })}
                disabled={!isAdmin}
                placeholder="e.g. 3.50"
                data-testid="edit-recipe-price" />
            </div>
            <div className="col-span-2">
              <Label>Output Product (till PLU)</Label>
              {/* iter381 — replaced raw numeric input with a searchable
                  picker. The auditor can find products by name or code
                  instead of memorising PIDs. Empty = recipe is not
                  mapped to a finished product yet. */}
              <div className="flex items-center gap-2">
                <div className="flex-1 min-w-0">
                  <ProductPicker
                    products={products}
                    value={form.output_product_id}
                    onChange={(pid) => updateForm({ output_product_id: pid || '' })}
                    disabled={!isAdmin || products.length === 0}
                    placeholder={products.length === 0 ? 'Loading products…' : 'Search master to map output product…'}
                    testId="edit-recipe-output-pid"
                  />
                </div>
                {form.output_product_id && (
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    onClick={() => updateForm({ output_product_id: '' })}
                    disabled={!isAdmin}
                    data-testid="edit-recipe-output-pid-clear"
                  >
                    Clear
                  </Button>
                )}
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <input id="edit-recipe-active" type="checkbox"
              checked={form.is_active}
              onChange={e => updateForm({ is_active: e.target.checked })}
              disabled={!isAdmin} data-testid="edit-recipe-active" />
            <Label htmlFor="edit-recipe-active" className="cursor-pointer">
              Active (uncheck to deactivate without deleting)
            </Label>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <h3 className="font-semibold text-sm">Ingredients ({form.ingredients.length})</h3>
              <Button size="sm" variant="outline" onClick={addIngredient} disabled={!isAdmin}
                data-testid="edit-recipe-add-ingredient">+ Add</Button>
            </div>
            {form.ingredients.length === 0 && (
              <p className="text-xs text-muted-foreground">No ingredients yet.</p>
            )}
            <div className="space-y-2">
              {form.ingredients.map((ing, idx) => (
                <div key={idx} className="border rounded p-2 grid grid-cols-12 gap-2 items-end bg-muted/20"
                  data-testid={`edit-recipe-ingredient-${idx}`}>
                  <div className="col-span-4">
                    <Label className="text-xs">Label / display name</Label>
                    <Input value={ing.ingredient_label}
                      onChange={e => updateIngredient(idx, { ingredient_label: e.target.value })}
                      disabled={!isAdmin} placeholder="e.g. Coca-Cola Syrup BIB" />
                    {/* iter381 — product mapping picker. The label above is
                        a free-text display name used in the recipe; the
                        picker below ties it to the actual inventory
                        master product so sales drive stock consumption.
                        Non-stock ingredients (water, ice) skip the
                        picker by ticking "Non-stock". */}
                    <div className="mt-1.5 flex items-center gap-2">
                      <div className="flex-1 min-w-0">
                        <ProductPicker
                          products={products}
                          value={ing.ingredient_product_id}
                          onChange={(pid) => updateIngredient(idx, {
                            ingredient_product_id: pid ? Number(pid) : null,
                            ingredient_product_name: pid
                              ? (products.find(p => String(p.id) === String(pid))?.product_name || null)
                              : null,
                          })}
                          disabled={!isAdmin || ing.is_non_stock || products.length === 0}
                          placeholder={ing.is_non_stock
                            ? 'Non-stock — no mapping needed'
                            : (products.length === 0 ? 'Loading…' : 'Map to inventory product…')}
                          testId={`edit-recipe-ingredient-product-${idx}`}
                        />
                      </div>
                      {ing.ingredient_product_id && !ing.is_non_stock && (
                        <Button
                          type="button"
                          size="sm"
                          variant="ghost"
                          onClick={() => updateIngredient(idx, {
                            ingredient_product_id: null,
                            ingredient_product_name: null,
                          })}
                          disabled={!isAdmin}
                          data-testid={`edit-recipe-ingredient-product-clear-${idx}`}
                        >
                          Clear
                        </Button>
                      )}
                    </div>
                    {ing.ingredient_product_id && !ing.is_non_stock && (
                      <p className="text-[11px] text-emerald-700 mt-0.5">
                        ✓ Linked to product <code>#{ing.ingredient_product_id}</code>
                        {ing.ingredient_product_name ? ` — ${ing.ingredient_product_name}` : ''}
                      </p>
                    )}
                    {!ing.ingredient_product_id && !ing.is_non_stock && (
                      <p className="text-[11px] text-amber-700 mt-0.5">
                        ⚠ Not mapped — sales of this recipe won't consume any stock for this ingredient.
                      </p>
                    )}
                  </div>
                  <div className="col-span-2">
                    <Label className="text-xs">Qty</Label>
                    <Input type="number" step="0.001" value={ing.ingredient_qty}
                      onChange={e => updateIngredient(idx, { ingredient_qty: e.target.value })}
                      disabled={!isAdmin}
                      placeholder="e.g. 30" />
                  </div>
                  <div className="col-span-2">
                    <Label className="text-xs">Unit</Label>
                    <select className="w-full border rounded h-9 px-2 text-sm bg-white"
                      value={ing.ingredient_unit}
                      onChange={e => updateIngredient(idx, { ingredient_unit: e.target.value })}
                      disabled={!isAdmin}>
                      <option value="ml">ml</option>
                      <option value="cl">cl</option>
                      <option value="oz">oz</option>
                      <option value="part">part</option>
                      <option value="g">g</option>
                      <option value="ea">ea</option>
                    </select>
                  </div>
                  <div className="col-span-2 flex items-center gap-1 pt-4">
                    <input id={`ing-nonstock-${idx}`} type="checkbox"
                      checked={ing.is_non_stock}
                      onChange={e => updateIngredient(idx, { is_non_stock: e.target.checked })}
                      disabled={!isAdmin} />
                    <Label htmlFor={`ing-nonstock-${idx}`} className="text-xs cursor-pointer">Non-stock</Label>
                  </div>
                  <div className="col-span-2 text-right">
                    <Button size="sm" variant="destructive"
                      onClick={() => removeIngredient(idx)} disabled={!isAdmin}
                      data-testid={`edit-recipe-remove-ingredient-${idx}`}>Remove</Button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <h3 className="font-semibold text-sm">Servings ({form.servings.length})</h3>
              <Button size="sm" variant="outline" onClick={addServing} disabled={!isAdmin}
                data-testid="edit-recipe-add-serving">+ Add</Button>
            </div>
            <div className="space-y-2">
              {form.servings.map((srv, idx) => (
                <div key={idx} className="border rounded p-2 grid grid-cols-12 gap-2 items-end bg-muted/20"
                  data-testid={`edit-recipe-serving-${idx}`}>
                  <div className="col-span-3">
                    <Label className="text-xs">Label</Label>
                    <Input value={srv.serving_label}
                      onChange={e => updateServing(idx, { serving_label: e.target.value })}
                      placeholder="e.g. 22 oz" disabled={!isAdmin} />
                  </div>
                  <div className="col-span-2">
                    <Label className="text-xs">ml</Label>
                    <Input type="number" step="0.01" value={srv.serving_ml}
                      onChange={e => updateServing(idx, { serving_ml: e.target.value })}
                      disabled={!isAdmin}
                      placeholder="e.g. 650" />
                  </div>
                  <div className="col-span-3">
                    <Label className="text-xs">Price (£)</Label>
                    <Input type="number" step="0.01" value={srv.selling_price_incl_vat}
                      onChange={e => updateServing(idx, { selling_price_incl_vat: e.target.value })}
                      disabled={!isAdmin}
                      placeholder="e.g. 3.50" />
                  </div>
                  <div className="col-span-2 flex items-center gap-1 pt-4">
                    <input id={`srv-default-${idx}`} type="radio" name="default-serving"
                      checked={srv.is_default}
                      onChange={() => updateServing(idx, { is_default: true })}
                      disabled={!isAdmin} />
                    <Label htmlFor={`srv-default-${idx}`} className="text-xs cursor-pointer">Default</Label>
                  </div>
                  <div className="col-span-2 text-right">
                    <Button size="sm" variant="destructive"
                      onClick={() => removeServing(idx)}
                      disabled={!isAdmin || form.servings.length === 1}
                      data-testid={`edit-recipe-remove-serving-${idx}`}>Remove</Button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div>
            <Label>Notes</Label>
            <Textarea value={form.notes} onChange={e => updateForm({ notes: e.target.value })}
              rows={2} disabled={!isAdmin}
              placeholder="e.g. Coke Freestyle dispenser, ratio 5:1 carb-to-syrup"
              data-testid="edit-recipe-notes" />
          </div>
        </div>

        <div className="sticky bottom-0 bg-white border-t px-6 py-3 flex justify-end gap-2 z-10">
          <Button variant="ghost" onClick={() => onClose(false)} disabled={busy}>Cancel</Button>
          <Button onClick={save} disabled={!isAdmin || busy} data-testid="edit-recipe-save">
            {busy ? 'Saving…' : (isCreate ? 'Create recipe' : 'Save changes')}
          </Button>
        </div>
      </div>
    </div>
  );
}

